import { round } from 'lodash';

import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
} from 'react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { Transition } from 'react-transition-group';
import styled, { useTheme } from 'styled-components';

import { useAppDispatch } from '../../hooks/useAppDispatcher.hook';
import { useAppSelector } from '../../hooks/useAppSelector.hook';
import { useModalManager } from '../../hooks/ModalManager.hook';
import useAuthHook from '../../hooks/useAuth.hook';

import {
  deleteRecordInfoThunk,
  getCandidateRecordInfoThunk,
  getCandidateRecordListThunk,
} from '../../redux/Candidate/Record/Record.thunk';

import { ALERT_ACTION_ENUM } from '../../types/action.types';
import { UserPermissions } from '../../types/userPermissions.types';
import {
  PersonRecord,
  PersonRecordType,
} from '../../redux/Candidate/Record/Record.type';

import NewNotificationModalComponent from '../Modal/Notification/NewNotificationModal.component';
import NotificationsModalComponent from '../Modal/Notifications/NotificationsModal.component';
import RecordRemoveAlertComponent from '../Alert/Candidate/Record/RecordRemoveAlert.component';
import DropdownComponent from '../Dropdown/Dropdown.component';
import DropdownItemComponent, {
  StyledDropdownItem,
  StyledDropdownItemLeft,
} from '../Dropdown/DropdownItem.component';
import RecordEditModalComponent from '../Modal/Candidate/Record/RecordEditModal.component';
import CandidateRecordHistoryBlockComponent from './CandidateRecordHistoryBlock.component';
import ButtonComponent, { StyledButtonContentLeft } from '../Button.component';
import CardComponent from '../Card.component';
import CandidateRecordInfoBlockComponent, {
  StyledCandidateRecordInfo,
} from './CandidateRecordInfoBlock.component';
import IconButtonComponent from '../IconButton.component';
import PortalComponent from '../Portal.component';

import { checkUserRequiredPermissions } from '../../utils/getUserRolesByToken';
import { dateFormatter } from '../../utils/formatters';

import CloneIcon from '../Icons/Clone.icon';
import TrashIcon from '../Icons/Trash.icon';
import BallIcon from '../Icons/Ball.icon';
import PlusIcon from '../Icons/Plus.icon';
import Burger2Icon from '../Icons/Burger2.icon';
import CalendarIcon from '../Icons/Calendar.icon';
import { StyledBaseIcon } from '../Icons/Base.icon';
import ChevronRightIcon from '../Icons/ChevronRight.icon';
import PenIcon from '../Icons/Pen.icon';
import BurgerIcon from '../Icons/Burger.icon';

import { DATE_FORMAT, TIME_FORMAT } from '../../constants/date.contant';
import AttachmentsBlockComponent from './AttachmentsBlockComponent.component';
import { getNotificationCounterThunk } from '../../redux/Notification/NotificationThunk';
import ReplyToCandidateModal, {
  GOOGLE_CALENDAR_EVENT_WHEN_STATUSES,
  REPLY_WHEN_STATUSES,
} from '../Modal/ReplyToCandidateModal.component';
import { getPersonRecordHistoryLinkThunk } from '../../redux/Candidate/Record/History/History.thunk';
import { HISTORY_PER_PAGE } from '../../redux/Candidate/Record/History.constant';
import LastRecordRemoveAlertComponent from '../Alert/Candidate/Record/LastRecordRemoveAlert.component';
import GoogleCalendarEventModalComponent from '../Modal/Calendar/GoogleCalendarEventModal.component';
import { getCandidateSelected } from '../../redux/Candidate/Candidate.slice';

export interface CandidateRecordProps {
  info: PersonRecord;
  showHistory?: boolean;
  deleteWithCandidate?: boolean;
}

const StyledDeleteDropdownItem = styled.div`
  ${StyledDropdownItem}, ${StyledDropdownItemLeft} {
    color: ${({ theme }) => theme.colors.RED.R400};

    :hover {
      color: ${({ theme }) => theme.colors.RED.R500};
    }
  }
`;
const StyledCandidateRecordHistoryShowButton = styled.div<{
  isShow: boolean;
}>`
  display: flex;
  margin: 0 0 0 40px;

  ${StyledButtonContentLeft} {
    ${props => props.isShow && `transform: rotate(90deg)`};
  }
`;
const StyledCandidateRecordHeaderActions = styled.div`
  display: flex;
  gap: 15px;
  color: ${({ theme }) => theme.colors.LABEL};
`;
const StyledCandidateRecordHeaderStatusDatetimeDate = styled.div`
  padding: 0 8px;
`;
const StyledCandidateRecordHeaderStatusDatetime = styled.div<{
  isExpired: boolean;
}>`
    display: flex;
    align-items: center;
    color: ${({ theme }) => theme.colors.LABEL};

    ${StyledBaseIcon} {
        color: ${({ theme }) => theme.colors.BLUE.B400};
        ${props => props.isExpired && `color: ${props.theme.colors.RED.R400}`}
`;
const StyledCandidateRecordHeaderStatusStatus = styled.div`
  color: ${({ theme }) => theme.colors.LABEL};
`;
const StyledCandidateRecordHeaderStatusSeparator = styled.div`
  border-radius: 3px;
  width: 6px;
  height: 6px;
  background: ${({ theme }) => theme.colors.BORDER};
`;
const StyledCandidateRecordHeaderStatusEvent = styled.div``;
const StyledCandidateRecordHeaderStatus = styled.div`
  padding: 2px 0;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 10px;
`;
const StyledCandidateRecordHeaderSub = styled.div`
  display: flex;
  align-items: center;
  gap: 30px;
`;
const StyledCandidateRecordHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
`;
const StyledCandidateRecordDateTime = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.LABEL};
`;
const StyledCandidateRecordCR = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;
const StyledCandidateRecordCL = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  padding: 5px 0 0 0;
`;
const StyledCandidateRecord = styled.div`
  display: grid;
  grid-template-columns: 90px auto;
  gap: 10px;
  padding: 15px 20px;

  ${StyledCandidateRecordInfo} {
    margin: -15px 0 0 0;
  }
`;

const CALENDAR_ICON_SIZE = 12;
const CandidateRecordBlockComponent = ({
  info,
  showHistory: globalShowHistory = false,
  deleteWithCandidate = false,
}: CandidateRecordProps) => {
  const dispatch = useAppDispatch();
  const theme = useTheme() as any;
  const { t } = useTranslation();
  const burgerRef = useRef(null);
  const ballRef = useRef(null);
  const [isLoad, setLoad] = useState(false);
  const { openModal } = useModalManager();
  const recordHistory = useAppSelector(state => state.personHistory.list);
  const [showHistory, setShowHistory] = useState(false);
  const [isExpired, setExpired] = useState(false);
  const [isOpenDropdown, setOpenDropdown] = useState(false);
  const [isOpenNotification, setOpenNotification] = useState(false);
  const { user } = useAuthHook();
  const formCandidate = useAppSelector(getCandidateSelected);

  useEffect(() => {
    if (!info || !info.date) return;
    setExpired(round(new Date().getTime() / 1000) > info.date);
  }, [info]);

  const [canCreateRecord, canDeleteRecord, canEditRecord, canCreateMeet] =
    useMemo(() => {
      return [
        checkUserRequiredPermissions(user.permissions, [
          UserPermissions.CAN_CREATE_CANDIDATE_RECORD,
        ]),
        checkUserRequiredPermissions(user.permissions, [
          UserPermissions.CAN_DELETE_CANDIDATE_RECORD,
        ]),
        checkUserRequiredPermissions(user.permissions, [
          UserPermissions.CAN_EDIT_CANDIDATE_RECORD,
        ]),
        checkUserRequiredPermissions(user.permissions, [
          UserPermissions.CAN_CREATE_GOOGLE_MEET_INTERVIEW,
        ]),
      ];
    }, [user]);

  const handleOpenDropdown = () => {
    setOpenDropdown(true);
  };
  const handleOpenNotifications = () => {
    setOpenNotification(true);
  };

  const handleOpenEditModal = () => {
    openModal(
      RecordEditModalComponent,
      {
        preInfo: {
          id: info.id,
          personId: info.personId,
        },
        isEditCandidatePersonalCard: true,
      },
      {
        disableClose: false,
      },
    );
    setOpenDropdown(false);
  };

  const handleLoadMore = useCallback(
    (recordId: number, items: any[]) => {
      setLoad(true);
      dispatch(
        getPersonRecordHistoryLinkThunk({
          records: [recordId],
          personId: info.id,
          page: 1,
          perPage: items.length + HISTORY_PER_PAGE,
        }),
      ).then(() => {
        setLoad(false);
      });
    },
    [dispatch],
  );

  const handleDuplicateEditModal = () => {
    openModal(
      RecordEditModalComponent,
      {
        info: {
          ...info,
          id: 0,
          originalId: info.id,
          attachments: [],
          comment: '',
        },
        needAllStatuses: true,
      },
      {
        disableClose: false,
      },
    );
    setOpenDropdown(false);
  };

  const handleOpenNewNotificationModal = useCallback(() => {
    if (!info) return;
    // @ts-ignore
    openModal(NewNotificationModalComponent, {
      targetType: 'App\\Models\\PersonRecord',
      targetId: info.id!,
      onFinally: () => {
        // TODO: выяснить: зачем опять делать getCandidateRecordListThunk?
        // TODO: выяснить: вызывается и тут тоже const NotificationsModalComponent = (restProps:
        dispatch(getCandidateRecordListThunk({ personId: info.personId! }));
        dispatch(getNotificationCounterThunk());
      },
    });

    setOpenNotification(false);
  }, [isOpenNotification, info]);

  const handleOpenNotificationListModal = useCallback(() => {
    if (!info) return;
    // @ts-ignore
    openModal(
      NotificationsModalComponent,
      {
        targetType: 'App\\Models\\PersonRecord',
        targetId: info.id!,
        personId: info.personId!,
        isAccessDelete: true,
      },
      {
        position: 'manual',
      },
    );

    setOpenNotification(false);
  }, [isOpenNotification, info]);

  const canViewGoogleMeetDropdown = useMemo(() => {
    if (!info?.status) return false;

    return (
      GOOGLE_CALENDAR_EVENT_WHEN_STATUSES.includes(info.status.code || '') &&
      info.status.hasPlanningTime
    );
  }, [info?.status]);

  const onHandleNewMeetDropDown = async () => {
    if (!info.status) return;

    try {
      const result = await dispatch(
        getCandidateRecordInfoThunk({
          personId: info.personId,
          recordId: info.id,
        }),
      ).unwrap();

      const candidate = formCandidate.item;

      openModal(GoogleCalendarEventModalComponent, {
        eventStatusCode: info.status?.code || '',
        record: result,
        candidateName: candidate.fullName,
        candidateEmail: candidate.email,
        candidateAttachments: candidate.attachments,
      });
    } catch (e) {
      console.error(e);
    }
  };

  const handleConfirmDeleteAlert = () => {
    const deleteRecordModal = deleteWithCandidate
      ? LastRecordRemoveAlertComponent
      : RecordRemoveAlertComponent;
    openModal(deleteRecordModal, {
      handleConfirm: handleConfirmDelete,
    });
    setOpenDropdown(false);
  };

  const handleConfirmDelete = (confirm?: ALERT_ACTION_ENUM) => {
    confirm &&
      confirm === ALERT_ACTION_ENUM.YES &&
      dispatch(
        deleteRecordInfoThunk({
          personId: info.personId,
          recordId: info.id,
        }),
      );
  };

  const openReplyToCandidateModal = () => {
    if (!info.status) return;

    openModal(ReplyToCandidateModal, {
      status: info.status,
    });
  };

  return (
    <React.Fragment>
      <CardComponent key={info.id}>
        <StyledCandidateRecord>
          <StyledCandidateRecordCL>
            <StyledCandidateRecordDateTime>
              {dayjs(info.createdAt).format(DATE_FORMAT)}
            </StyledCandidateRecordDateTime>
            <StyledCandidateRecordDateTime>
              {dayjs(info.createdAt).format(TIME_FORMAT)}
            </StyledCandidateRecordDateTime>
          </StyledCandidateRecordCL>

          <StyledCandidateRecordCR>
            <StyledCandidateRecordHeader>
              <StyledCandidateRecordHeaderSub>
                <StyledCandidateRecordHeaderStatus>
                  {info.type === PersonRecordType.LABEL ? (
                    <StyledCandidateRecordHeaderStatusEvent
                      style={{ color: theme.colors.ORANGE.O400 }}
                    >
                      {t('Tagging')}
                    </StyledCandidateRecordHeaderStatusEvent>
                  ) : (
                    <React.Fragment>
                      <StyledCandidateRecordHeaderStatusEvent
                        style={{
                          color: info.status && info.status.event.color,
                        }}
                      >
                        {info.status
                          ? info.status.event.name
                          : t('Unknown status')}
                      </StyledCandidateRecordHeaderStatusEvent>
                      {info.status && (
                        <React.Fragment>
                          <StyledCandidateRecordHeaderStatusSeparator />
                          <StyledCandidateRecordHeaderStatusStatus
                            style={{ color: info.status.color }}
                          >
                            {info.status.name}
                          </StyledCandidateRecordHeaderStatusStatus>
                          {info.date && (
                            <StyledCandidateRecordHeaderStatusDatetime
                              isExpired={isExpired}
                            >
                              <CalendarIcon
                                width={CALENDAR_ICON_SIZE}
                                height={CALENDAR_ICON_SIZE}
                              />
                              <StyledCandidateRecordHeaderStatusDatetimeDate>
                                {dateFormatter(info.date, DATE_FORMAT)}
                              </StyledCandidateRecordHeaderStatusDatetimeDate>
                              {dateFormatter(info.date, TIME_FORMAT)}
                            </StyledCandidateRecordHeaderStatusDatetime>
                          )}
                        </React.Fragment>
                      )}
                    </React.Fragment>
                  )}
                </StyledCandidateRecordHeaderStatus>
                {REPLY_WHEN_STATUSES.includes(info.status?.code || '') && (
                  <ButtonComponent
                    btnSize="small"
                    handleClick={openReplyToCandidateModal}
                  >
                    {t('sendEmail')}
                  </ButtonComponent>
                )}
              </StyledCandidateRecordHeaderSub>
              <ActionsPanel
                hasNotificationsButton={checkUserRequiredPermissions(
                  user.permissions,
                  [UserPermissions.CAN_VIEW_EVENT],
                )}
                onNotificationsButtonClick={handleOpenNotifications}
                notificationsCount={info.notifications_counter}
                ballRef={ballRef}
                canEditRecord={canEditRecord}
                onOpenEditModal={handleOpenEditModal}
                canDeleteRecord={canDeleteRecord}
                canCreateRecord={canCreateRecord}
                burgerRef={burgerRef}
                onOpenDropdown={handleOpenDropdown}
              />
            </StyledCandidateRecordHeader>
            <CandidateRecordInfoBlockComponent info={info} />
            {info.attachments && info.attachments.length > 0 && (
              <AttachmentsBlockComponent
                withAllWidth
                attachments={info.attachments}
                wrap={false}
              />
            )}
          </StyledCandidateRecordCR>
        </StyledCandidateRecord>
        <Transition
          in={isOpenDropdown}
          mountOnEnter={true}
          unmountOnExit={true}
          timeout={{
            exit: 300,
          }}
        >
          {state => (
            <PortalComponent
              withOverlay={false}
              onClose={() => setOpenDropdown(false)}
              position="manual"
              modalStyle={{
                ...theme.animations.defaultState,
                ...theme.animations.defaultSteps[state],
              }}
            >
              <DropdownComponent getRef={burgerRef}>
                {canCreateRecord && (
                  <DropdownItemComponent
                    left={<CloneIcon />}
                    handleClick={handleDuplicateEditModal}
                  >
                    {t('Duplicate record')}
                  </DropdownItemComponent>
                )}
                {canCreateMeet && canViewGoogleMeetDropdown && (
                  <DropdownItemComponent
                    left={<PlusIcon />}
                    handleClick={onHandleNewMeetDropDown}
                  >
                    {t('New Meet')}
                  </DropdownItemComponent>
                )}
                {canDeleteRecord && (
                  <StyledDeleteDropdownItem>
                    <DropdownItemComponent
                      left={<TrashIcon />}
                      handleClick={handleConfirmDeleteAlert}
                    >
                      {t('Delete')}
                    </DropdownItemComponent>
                  </StyledDeleteDropdownItem>
                )}
              </DropdownComponent>
            </PortalComponent>
          )}
        </Transition>
        <Transition
          in={isOpenNotification}
          mountOnEnter={true}
          unmountOnExit={true}
          timeout={{
            exit: 300,
          }}
        >
          {state => (
            <PortalComponent
              withOverlay={false}
              onClose={() => setOpenNotification(false)}
              position="manual"
              modalStyle={{
                ...theme.animations.defaultState,
                ...theme.animations.defaultSteps[state],
              }}
            >
              <DropdownComponent getRef={ballRef}>
                <DropdownItemComponent
                  left={<Burger2Icon />}
                  handleClick={handleOpenNotificationListModal}
                >
                  {t('Open list')}
                </DropdownItemComponent>
                {checkUserRequiredPermissions(user.permissions, [
                  UserPermissions.CAN_CREATE_EVENT,
                ]) && (
                  <DropdownItemComponent
                    left={<PlusIcon />}
                    handleClick={handleOpenNewNotificationModal}
                  >
                    {t('Add new')}
                  </DropdownItemComponent>
                )}
              </DropdownComponent>
            </PortalComponent>
          )}
        </Transition>
      </CardComponent>
      {!globalShowHistory && info.history_counter > 0 ? (
        <StyledCandidateRecordHistoryShowButton isShow={showHistory}>
          <ButtonComponent
            disabled={recordHistory.loading}
            left={<ChevronRightIcon />}
            align="left"
            handleClick={() => setShowHistory(!showHistory)}
          >
            {t(showHistory ? 'Hide record history' : 'Display record history')}
          </ButtonComponent>
        </StyledCandidateRecordHistoryShowButton>
      ) : (
        <div style={{ padding: '10px 0' }} />
      )}
      {recordHistory.items &&
        recordHistory.items[info.id] && // @ts-ignore
        recordHistory.items[info.id].map(history => (
          <Transition
            key={history.uuid}
            in={globalShowHistory || showHistory}
            timeout={{ exit: 300 }}
            mountOnEnter
            unmountOnExit
          >
            {state => (
              <CandidateRecordHistoryBlockComponent
                info={history}
                style={{
                  ...theme.animations.slideState,
                  ...theme.animations.slideSteps[state],
                }}
              />
            )}
          </Transition>
        ))}
      {showHistory &&
        recordHistory.items && // @ts-ignore
        recordHistory.items[info.id]?.length && // @ts-ignore
        recordHistory.haveMore.includes(info.id) && (
          <ButtonComponent
            disabled={isLoad}
            handleClick={() =>
              handleLoadMore(info.id, recordHistory.items[info.id])
            }
          >
            {t('loadMore')}
          </ButtonComponent>
        )}
    </React.Fragment>
  );
};

export default CandidateRecordBlockComponent;

const ActionsPanel = ({
  hasNotificationsButton,
  onNotificationsButtonClick,
  notificationsCount,
  ballRef, // ! what for?
  burgerRef, // ! what for?
  canEditRecord,
  onOpenEditModal,
  onOpenDropdown,
  canDeleteRecord,
  canCreateRecord,
}: {
  hasNotificationsButton: boolean;
  onNotificationsButtonClick: () => void;
  notificationsCount: number;
  ballRef: React.MutableRefObject<null>;
  burgerRef: React.MutableRefObject<null>;
  canEditRecord: boolean;
  onOpenEditModal: () => void;
  onOpenDropdown: () => void;
  canDeleteRecord: boolean;
  canCreateRecord: boolean;
}) => {
  return (
    <div style={{ border: 'px solid pink' }}>
      <StyledCandidateRecordHeaderActions>
        {hasNotificationsButton && (
          <IconButtonComponent
            badge={notificationsCount}
            badgeAppearance="blue"
            getRef={ballRef} // ! what for?
            onClick={onNotificationsButtonClick}
          >
            <BallIcon />
          </IconButtonComponent>
        )}
        {canEditRecord && (
          <IconButtonComponent onClick={onOpenEditModal}>
            <PenIcon />
          </IconButtonComponent>
        )}
        {canDeleteRecord &&
          canCreateRecord && ( // TODO: why two? SINGLE RESPONSIBILITY PRINCIPLE
            <IconButtonComponent getRef={burgerRef} onClick={onOpenDropdown}>
              <BurgerIcon />
            </IconButtonComponent>
          )}
      </StyledCandidateRecordHeaderActions>
    </div>
  );
};
